import React, { useState, useRef, useEffect } from "react";
import { X, Image as ImageIcon } from "lucide-react";
import { apiClient } from "@/services/api";
import { useTranslation } from "react-i18next";
import { formatFileSize } from "@/utils/fileUtils";

interface EventImageUploadProps {
  eventId?: string;
  currentImageUrl?: string;
  onImageUploaded?: (imageKey: string, imageSize: number) => void;
  onImageDeleted?: () => void;
  onImageSelected?: (file: File) => void; // For creation mode
  onImageRemoved?: () => void; // For creation mode
  className?: string;
}

export const EventImageUpload: React.FC<EventImageUploadProps> = ({
  eventId,
  currentImageUrl,
  onImageUploaded,
  onImageDeleted,
  onImageSelected,
  onImageRemoved,
  className = "",
}) => {
  const { t } = useTranslation(["events", "common"]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    currentImageUrl || null,
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "image/heic",
    "image/heif",
    "image/gif",
  ];

  // Sync internal previewUrl state with currentImageUrl prop
  useEffect(() => {
    setPreviewUrl(currentImageUrl || null);
  }, [currentImageUrl]);

  // Don't leave a pending success-dismiss timer behind on unmount
  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const scheduleSuccessDismiss = () => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
    }
    successTimerRef.current = setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(t("events:imageUpload.sizeExceeded"));
      return;
    }

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type.toLowerCase())) {
      setError(t("events:imageUpload.invalidType"));
      return;
    }

    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    // In edit mode (eventId exists), auto-upload immediately
    if (eventId) {
      setIsUploading(true);
      setSuccessMessage(null);
      try {
        const result = await apiClient.uploadEventImage(eventId, file);
        setSelectedFile(null);
        setSuccessMessage(t("events:imageUpload.uploadSuccess"));

        // Clear success message after 3 seconds
        scheduleSuccessDismiss();

        if (onImageUploaded) {
          onImageUploaded(result.imageKey, result.imageSize);
        }
      } catch (err) {
        console.error("Error uploading image:", err);
        setError(t("events:imageUpload.uploadFailed"));
        setPreviewUrl(currentImageUrl || null);
        setSelectedFile(null);
      } finally {
        setIsUploading(false);
      }
    } else if (onImageSelected) {
      // In creation mode (no eventId), notify parent
      onImageSelected(file);
    }
  };

  const handleDelete = async () => {
    if (!eventId) return;

    setIsDeletingImage(true);
    setError(null);

    try {
      await apiClient.deleteEventImage(eventId);
      setPreviewUrl(null);
      setSelectedFile(null);

      if (onImageDeleted) {
        onImageDeleted();
      }
    } catch (err) {
      console.error("Error deleting image:", err);
      setError(t("events:imageUpload.deleteFailed"));
    } finally {
      setIsDeletingImage(false);
    }
  };

  const handleRemoveSelection = () => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // In creation mode, clear preview completely
    // In edit mode, revert to the original image (if any)
    if (!eventId) {
      setPreviewUrl(null);
      if (onImageRemoved) {
        onImageRemoved();
      }
    } else {
      setPreviewUrl(currentImageUrl || null);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          {t("events:imageUpload.label")}
        </label>
        <p className="text-sm text-secondary-500 mb-3">
          {t("events:imageUpload.help")}
        </p>
      </div>

      {/* Preview Area */}
      {previewUrl && (
        <div className="relative">
          <img
            src={previewUrl}
            alt={t("events:imageUpload.alt")}
            className="w-full h-64 object-cover rounded-lg border border-secondary-300"
          />
          {eventId && !selectedFile && !isUploading && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeletingImage}
              className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={t("events:imageUpload.deleteTitle")}
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {isUploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
              <p className="text-white text-center">
                {t("events:imageUpload.uploading")}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Hidden file input - shared by all upload triggers */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload Area - shown when no preview */}
      {!previewUrl && (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-secondary-300 rounded-lg p-8 text-center hover:border-secondary-400 cursor-pointer transition-colors"
        >
          <div className="flex flex-col items-center">
            <ImageIcon className="w-10 h-10 text-secondary-400 mb-2" />
            <p className="text-sm text-secondary-600">
              {t("events:imageUpload.clickToUpload")}
            </p>
          </div>
        </div>
      )}

      {/* Change Image Button - shown when there's a preview in edit mode */}
      {previewUrl && eventId && !selectedFile && !isUploading && (
        <div className="text-center">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="text-sm text-primary-600 hover:text-primary-800 hover:underline"
          >
            {t("events:imageUpload.clickToChange")}
          </button>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-600">{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* File Selection Info - Creation Mode */}
      {selectedFile && !eventId && (
        <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-primary-900">
                {selectedFile.name}
              </p>
              <p className="text-xs text-primary-700">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            <button
              type="button"
              onClick={handleRemoveSelection}
              className="ml-3 text-primary-600 hover:text-primary-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-primary-600 mt-2">
            {t("events:imageUpload.willUploadAfterCreate")}
          </p>
        </div>
      )}
    </div>
  );
};
